import { useEffect, useMemo, useState, type ChangeEvent, type FormEvent } from 'react';
import {
  Building2,
  Check,
  ChevronRight,
  CircleAlert,
  Copy,
  ImagePlus,
  PauseCircle,
  Plus,
  Save,
  Search,
  ServerCog,
  ShieldCheck,
  Trash2,
  Users,
  X
} from 'lucide-react';
import { useLocation, useNavigate } from '../../app/router';
import { clearToken, readToken, superadminTokenKey } from '../../app/session';
import { CroatianDateInput } from '../../components/CroatianDateInput';
import { ApiError, apiAssetUrl, apiRequest } from '../../lib/api';
import {
  statusLabel,
  type Company,
  type CompanyDetail,
  type Module,
  type ProvisionResult
} from './types';

interface CompanyForm {
  legalName: string;
  displayName: string;
  oib: string;
  address: string;
  postalCode: string;
  city: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  hostname: string;
  primaryColor: string;
  secondaryColor: string;
  employeeLimit: number;
  trialEndsAt: string;
  licenseValidUntil: string;
  planName: string;
  adminFirstName: string;
  adminLastName: string;
  adminEmail: string;
  logoDataUrl: string;
  logoName: string;
  moduleKeys: string[];
}

interface DetailForm {
  legalName: string;
  displayName: string;
  address: string;
  postalCode: string;
  city: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  primaryColor: string;
  secondaryColor: string;
  employeeLimit: number;
  trialEndsAt: string;
  licenseValidUntil: string;
  planName: string;
  logoDataUrl: string;
  logoName: string;
  removeLogo: boolean;
}

function dateValue(value: string | null | undefined): string {
  return value ? value.slice(0, 10) : '';
}

export function createEmptyCompanyForm(
  modules: Module[],
  defaultEmployeeLimit = 20
): CompanyForm {
  return {
    legalName: '',
    displayName: '',
    oib: '',
    address: '',
    postalCode: '',
    city: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    hostname: '',
    primaryColor: '#1C3667',
    secondaryColor: '#32B8C6',
    employeeLimit: defaultEmployeeLimit,
    trialEndsAt: '',
    licenseValidUntil: '',
    planName: 'Prilagođeni paket',
    adminFirstName: '',
    adminLastName: '',
    adminEmail: '',
    logoDataUrl: '',
    logoName: '',
    moduleKeys: modules.filter((module) => module.isCore).map((module) => module.key)
  };
}

function detailForm(company: CompanyDetail): DetailForm {
  return {
    legalName: company.legalName,
    displayName: company.displayName,
    address: company.address,
    postalCode: company.postalCode,
    city: company.city,
    contactName: company.contactName ?? '',
    contactEmail: company.contactEmail ?? '',
    contactPhone: company.contactPhone ?? '',
    primaryColor: company.primaryColor,
    secondaryColor: company.secondaryColor,
    employeeLimit: company.employeeLimit,
    trialEndsAt: dateValue(company.trialEndsAt),
    licenseValidUntil: dateValue(company.licenseValidUntil),
    planName: company.planName,
    logoDataUrl: '',
    logoName: '',
    removeLogo: false
  };
}

async function imageFromInput(event: ChangeEvent<HTMLInputElement>): Promise<{
  dataUrl: string;
  name: string;
}> {
  const file = event.target.files?.[0];
  if (!file) return { dataUrl: '', name: '' };
  if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
    throw new Error('Odaberi PNG, JPG ili WebP sliku.');
  }
  if (file.size > 750_000) throw new Error('Logo smije imati najviše 750 KB.');
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('Logo nije moguće pročitati.'));
    reader.readAsDataURL(file);
  });
  return { dataUrl, name: file.name };
}

export function CompaniesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const token = readToken(superadminTokenKey);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [modules, setModules] = useState<Module[]>([]);
  const [defaultEmployeeLimit, setDefaultEmployeeLimit] = useState(20);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState<CompanyForm>(() => createEmptyCompanyForm([]));
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<ProvisionResult | null>(null);
  const [detail, setDetail] = useState<CompanyDetail | null>(null);
  const [edit, setEdit] = useState<DetailForm | null>(null);
  const [detailError, setDetailError] = useState('');
  const [detailBusy, setDetailBusy] = useState(false);
  const [detailModules, setDetailModules] = useState<string[]>([]);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const [companyResponse, moduleResponse, defaults] = await Promise.all([
        apiRequest<{ items: Company[] }>('/api/v1/superadmin/companies', {
          accessToken: token
        }),
        apiRequest<{ items: Module[] }>('/api/v1/superadmin/companies/modules', {
          accessToken: token
        }),
        apiRequest<{ employeeLimit: number }>(
          '/api/v1/superadmin/companies/defaults',
          {
            accessToken: token
          }
        )
      ]);
      setCompanies(companyResponse.items);
      setModules(moduleResponse.items);
      setDefaultEmployeeLimit(defaults.employeeLimit);
      return {
        companies: companyResponse.items,
        modules: moduleResponse.items,
        defaultEmployeeLimit: defaults.employeeLimit
      };
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) {
        clearToken(superadminTokenKey);
        navigate('/superadmin/login', { replace: true });
        return null;
      }
      setError(caught instanceof ApiError ? caught.message : 'Podaci nisu dostupni.');
      return null;
    } finally {
      setLoading(false);
    }
  }

  async function openDetail(companyId: string) {
    setDetailBusy(true);
    setDetailError('');
    try {
      const response = await apiRequest<CompanyDetail>(
        `/api/v1/superadmin/companies/${companyId}`,
        { accessToken: readToken(superadminTokenKey) }
      );
      setDetail(response);
      setEdit(detailForm(response));
      setDetailModules(response.enabledModules);
    } catch (caught) {
      setDetailError(
        caught instanceof ApiError ? caught.message : 'Detalji firme nisu dostupni.'
      );
    } finally {
      setDetailBusy(false);
    }
  }

  useEffect(() => {
    void load().then((loaded) => {
      if (!loaded) return;
      const params = new URLSearchParams(location.search);
      if (params.get('new') === '1') {
        setForm(
          createEmptyCompanyForm(
            loaded.modules,
            loaded.defaultEmployeeLimit
          )
        );
        setResult(null);
        setModalOpen(true);
      }
      const companyId = params.get('company');
      if (companyId) void openDetail(companyId);
    });
  }, []);

  const visibleCompanies = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return companies;
    return companies.filter((company) =>
      [company.displayName, company.legalName, company.oib, company.city]
        .join(' ')
        .toLowerCase()
        .includes(query)
    );
  }, [companies, search]);

  function openNewCompany() {
    setForm(createEmptyCompanyForm(modules, defaultEmployeeLimit));
    setResult(null);
    setError('');
    setModalOpen(true);
  }

  function closeModal() {
    if (submitting) return;
    setModalOpen(false);
    setResult(null);
    setError('');
    setForm(createEmptyCompanyForm(modules, defaultEmployeeLimit));
  }

  function closeDetail() {
    if (detailBusy) return;
    setDetail(null);
    setEdit(null);
    setDetailError('');
    navigate('/superadmin/companies', { replace: true });
  }

  function toggleCreateModule(module: Module) {
    if (module.isCore || module.status !== 'ACTIVE') return;
    setForm((current) => ({
      ...current,
      moduleKeys: current.moduleKeys.includes(module.key)
        ? current.moduleKeys.filter((key) => key !== module.key)
        : [...current.moduleKeys, module.key]
    }));
  }

  function toggleDetailModule(module: Module) {
    if (module.isCore || module.status !== 'ACTIVE') return;
    setDetailModules((current) =>
      current.includes(module.key)
        ? current.filter((key) => key !== module.key)
        : [...current, module.key]
    );
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const response = await apiRequest<ProvisionResult>(
        '/api/v1/superadmin/companies',
        {
          method: 'POST',
          accessToken: token,
          body: JSON.stringify({
            legalName: form.legalName,
            displayName: form.displayName,
            oib: form.oib,
            address: form.address,
            postalCode: form.postalCode,
            city: form.city,
            countryCode: 'HR',
            contactName: form.contactName || undefined,
            contactEmail: form.contactEmail || undefined,
            contactPhone: form.contactPhone || undefined,
            logoDataUrl: form.logoDataUrl || undefined,
            primaryColor: form.primaryColor,
            secondaryColor: form.secondaryColor,
            timezone: 'Europe/Zagreb',
            locale: 'hr-HR',
            employeeLimit: Number(form.employeeLimit),
            trialEndsAt: form.trialEndsAt || undefined,
            licenseValidUntil: form.licenseValidUntil || undefined,
            planName: form.planName,
            moduleKeys: form.moduleKeys,
            hostname: form.hostname,
            admin: {
              firstName: form.adminFirstName,
              lastName: form.adminLastName,
              email: form.adminEmail,
              employeeCode: 'ADMIN'
            }
          })
        }
      );
      setResult(response);
      await load();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : 'Firmu trenutačno nije moguće otvoriti.'
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function saveDetails(event: FormEvent) {
    event.preventDefault();
    if (!detail || !edit) return;
    setDetailBusy(true);
    setDetailError('');
    try {
      await apiRequest(`/api/v1/superadmin/companies/${detail.id}`, {
        method: 'PATCH',
        accessToken: token,
        body: JSON.stringify(edit)
      });
      await Promise.all([openDetail(detail.id), load()]);
    } catch (caught) {
      setDetailError(
        caught instanceof ApiError ? caught.message : 'Promjene nisu spremljene.'
      );
    } finally {
      setDetailBusy(false);
    }
  }

  async function saveModules() {
    if (!detail) return;
    setDetailBusy(true);
    setDetailError('');
    try {
      const response = await apiRequest<{ enabledModules: string[] }>(
        `/api/v1/superadmin/companies/${detail.id}/modules`,
        {
          method: 'PATCH',
          accessToken: token,
          body: JSON.stringify({ moduleKeys: detailModules })
        }
      );
      setDetailModules(response.enabledModules);
      await Promise.all([openDetail(detail.id), load()]);
    } catch (caught) {
      setDetailError(
        caught instanceof ApiError ? caught.message : 'Moduli nisu spremljeni.'
      );
    } finally {
      setDetailBusy(false);
    }
  }

  async function changeStatus(status: 'ACTIVE' | 'SUSPENDED' | 'CLOSED') {
    if (!detail) return;
    setDetailBusy(true);
    setDetailError('');
    try {
      await apiRequest(`/api/v1/superadmin/companies/${detail.id}/status`, {
        method: 'PATCH',
        accessToken: token,
        body: JSON.stringify({ status })
      });
      await Promise.all([openDetail(detail.id), load()]);
    } catch (caught) {
      setDetailError(
        caught instanceof ApiError ? caught.message : 'Status nije promijenjen.'
      );
    } finally {
      setDetailBusy(false);
    }
  }

  return (
    <>
      <header className="page-header">
        <div>
          <span className="eyebrow">Klijenti i licence</span>
          <h1>Firme</h1>
          <p>Dodaj firmu jednom, a zatim sve vodi kroz ovaj panel.</p>
        </div>
        <button className="button button--primary" onClick={openNewCompany}>
          <Plus size={18} /> Nova firma
        </button>
      </header>

      <section className="admin-kpis">
        <article><span>Aktivne firme</span><strong>{companies.filter((item) => item.status === 'ACTIVE').length}</strong><Building2 /></article>
        <article><span>Ugovoreni korisnici</span><strong>{companies.reduce((sum, item) => sum + item.employeeLimit, 0)}</strong><Users /></article>
        <article><span>Aktivni moduli</span><strong>{modules.filter((item) => item.status === 'ACTIVE').length}</strong><ShieldCheck /></article>
        <article><span>Za provjeru</span><strong>{companies.filter((item) => ['FAILED', 'SUSPENDED'].includes(item.status)).length}</strong><CircleAlert /></article>
      </section>

      <section className="panel">
        <div className="panel__toolbar">
          <div><h2>Sve firme</h2><p className="muted">Klikni red za branding, module, limit i status.</p></div>
          <label className="search">
            <Search size={18} />
            <input placeholder="Naziv, OIB ili mjesto…" value={search} onChange={(event) => setSearch(event.target.value)} />
          </label>
        </div>
        {error && !modalOpen && <div className="form-error panel-error">{error}</div>}
        {loading ? (
          <div className="table-empty">Učitavam firme…</div>
        ) : (
          <div className="company-table">
            <div className="company-table__head">
              <span>Firma</span><span>Status</span><span>Moduli</span><span>Limit</span><span />
            </div>
            {visibleCompanies.map((company) => (
              <button
                className="company-row"
                key={company.id}
                onClick={() => void openDetail(company.id)}
              >
                <span className="company-identity">
                  {company.logoUrl ? (
                    <img className="company-mark company-mark--image" src={apiAssetUrl(company.logoUrl) ?? ''} alt="" onError={(event) => { event.currentTarget.src = '/icons/sihterica-192-v2.png'; }} />
                  ) : (
                    <span className="company-mark" style={{ color: company.primaryColor }}>
                      {company.displayName.slice(0, 2).toUpperCase()}
                    </span>
                  )}
                  <span><strong>{company.displayName}</strong><small>{company.oib} · {company.city}</small></span>
                </span>
                <span className={`status status--${company.status.toLowerCase()}`}><i /> {statusLabel(company.status)}</span>
                <span className="module-pills">
                  {company.enabledModules.slice(0, 3).map((module) => <span key={module}>{module}</span>)}
                  {company.enabledModules.length > 3 && <span>+{company.enabledModules.length - 3}</span>}
                </span>
                <span className="limit-cell">{company.employeeLimit} korisnika</span>
                <span className="icon-button"><ChevronRight size={19} /></span>
              </button>
            ))}
            {!visibleCompanies.length && <div className="table-empty">Nema firmi koje odgovaraju pretrazi.</div>}
          </div>
        )}
      </section>

      {modalOpen && (
        <div className="modal-backdrop" role="presentation" onMouseDown={closeModal}>
          <section className="company-modal" role="dialog" aria-modal="true" onMouseDown={(event) => event.stopPropagation()}>
            <header className="company-modal__header">
              <div><span className="eyebrow">Jedan jednostavan postupak</span><h2>Nova firma</h2></div>
              <button className="icon-button" onClick={closeModal} aria-label="Zatvori"><X /></button>
            </header>
            {result ? (
              <div className="provision-success">
                <span className="success-mark"><Check size={30} /></span>
                <h3>{form.displayName} je spremna</h3>
                <p>Otvorene su baza, tablice, moduli, branding i početni administrator.</p>
                <dl>
                  <div><dt>Adresa</dt><dd>{result.hostname}</dd></div>
                  <div><dt>Administrator</dt><dd>{result.initialAdmin.email}</dd></div>
                  <div className="activation-row"><dt>Aktivacijski token</dt><dd>{result.initialAdmin.activationToken}</dd></div>
                </dl>
                <p className="security-note"><CircleAlert size={17} /> Token vrijedi 48 sati. Kopiraj ga prije zatvaranja.</p>
                <div className="modal-actions">
                  <button className="button button--secondary" onClick={() => void navigator.clipboard.writeText(result.initialAdmin.activationToken)}><Copy size={17} /> Kopiraj token</button>
                  <button className="button button--primary" onClick={closeModal}>Gotovo</button>
                </div>
              </div>
            ) : (
              <form onSubmit={submit}>
                <div className="form-section">
                  <SectionTitle number="1" title="Identitet firme" text="Pravni podaci, kontakt i logo koji se vide u aplikaciji." />
                  <div className="brand-editor">
                    <label className="logo-picker">
                      {form.logoDataUrl ? <img src={form.logoDataUrl} alt="Pregled logotipa" /> : <ImagePlus />}
                      <span><strong>{form.logoName || 'Dodaj logo firme'}</strong><small>PNG, JPG ili WebP · najviše 750 KB</small></span>
                      <input type="file" accept="image/png,image/jpeg,image/webp" onChange={(event) => void imageFromInput(event).then((image) => setForm((current) => ({ ...current, logoDataUrl: image.dataUrl, logoName: image.name }))).catch((caught) => setError(caught.message))} />
                    </label>
                    {form.logoDataUrl && <button type="button" className="text-button danger-text" onClick={() => setForm((current) => ({ ...current, logoDataUrl: '', logoName: '' }))}><Trash2 size={15} /> Makni</button>}
                  </div>
                  <div className="form-grid">
                    <Field wide label="Puni pravni naziv"><input value={form.legalName} onChange={(e) => setForm({ ...form, legalName: e.target.value })} required /></Field>
                    <Field label="Naziv u aplikaciji"><input value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} required /></Field>
                    <Field label="OIB"><input inputMode="numeric" pattern="[0-9]{11}" maxLength={11} value={form.oib} onChange={(e) => setForm({ ...form, oib: e.target.value.replace(/\D/g, '') })} required /></Field>
                    <Field wide label="Adresa"><input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} required /></Field>
                    <Field label="Poštanski broj"><input value={form.postalCode} onChange={(e) => setForm({ ...form, postalCode: e.target.value })} required /></Field>
                    <Field label="Mjesto"><input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} required /></Field>
                    <Field label="Kontakt osoba"><input value={form.contactName} onChange={(e) => setForm({ ...form, contactName: e.target.value })} /></Field>
                    <Field label="Kontakt telefon"><input value={form.contactPhone} onChange={(e) => setForm({ ...form, contactPhone: e.target.value })} /></Field>
                    <Field wide label="Kontakt e-mail"><input type="email" value={form.contactEmail} onChange={(e) => setForm({ ...form, contactEmail: e.target.value })} /></Field>
                    <Field wide label="Domena ili subdomena"><input placeholder="firma.sihterica.hr" value={form.hostname} onChange={(e) => setForm({ ...form, hostname: e.target.value.toLowerCase().trim() })} required /></Field>
                    <ColorField label="Primarna boja" value={form.primaryColor} onChange={(value) => setForm({ ...form, primaryColor: value })} />
                    <ColorField label="Naglasna boja" value={form.secondaryColor} onChange={(value) => setForm({ ...form, secondaryColor: value })} />
                  </div>
                </div>

                <div className="form-section">
                  <SectionTitle number="2" title="Paket i licenca" text="Limit, rok licence i osoba koja će dalje voditi radnike." />
                  <div className="form-grid">
                    <Field label="Naziv paketa"><input value={form.planName} onChange={(e) => setForm({ ...form, planName: e.target.value })} required /></Field>
                    <Field label="Limit aktivnih korisnika"><input type="number" min={1} max={500} value={form.employeeLimit} onChange={(e) => setForm({ ...form, employeeLimit: Number(e.target.value) })} required /></Field>
                    <Field label="Probno razdoblje do"><CroatianDateInput value={form.trialEndsAt} onValueChange={(value) => setForm({ ...form, trialEndsAt: value })} ariaLabel="Probno razdoblje do" /></Field>
                    <Field label="Licenca vrijedi do"><CroatianDateInput value={form.licenseValidUntil} onValueChange={(value) => setForm({ ...form, licenseValidUntil: value })} ariaLabel="Licenca vrijedi do" /></Field>
                    <Field label="Ime administratora"><input value={form.adminFirstName} onChange={(e) => setForm({ ...form, adminFirstName: e.target.value })} required /></Field>
                    <Field label="Prezime administratora"><input value={form.adminLastName} onChange={(e) => setForm({ ...form, adminLastName: e.target.value })} required /></Field>
                    <Field wide label="E-mail administratora"><input type="email" value={form.adminEmail} onChange={(e) => setForm({ ...form, adminEmail: e.target.value })} required /></Field>
                  </div>
                </div>

                <div className="form-section">
                  <SectionTitle number="3" title="Zakup modula" text="Odaberi mogućnosti; potrebne ovisnosti uključuju se automatski." />
                  <ModuleSelector modules={modules} selected={form.moduleKeys} onToggle={toggleCreateModule} />
                </div>
                {error && <div className="form-error modal-error">{error}</div>}
                <footer className="company-modal__footer">
                  <button className="button button--secondary" type="button" onClick={closeModal}>Odustani</button>
                  <button className="button button--primary" disabled={submitting}>{submitting ? 'Otvaram bazu i tablice…' : 'Otvori firmu'} <ServerCog size={18} /></button>
                </footer>
              </form>
            )}
          </section>
        </div>
      )}

      {(detail || detailBusy) && (
        <div className="drawer-backdrop" onMouseDown={closeDetail}>
          <aside className="company-drawer" onMouseDown={(event) => event.stopPropagation()}>
            <header className="drawer-header">
              <div><span className="eyebrow">Upravljanje firmom</span><h2>{detail?.displayName ?? 'Učitavam…'}</h2></div>
              <button className="icon-button" onClick={closeDetail}><X /></button>
            </header>
            {detail && edit ? (
              <div className="drawer-body">
                <div className="company-hero">
                  {edit.logoDataUrl || (!edit.removeLogo && detail.logoUrl) ? <img src={edit.logoDataUrl || apiAssetUrl(detail.logoUrl)!} alt="" onError={(event) => { event.currentTarget.src = '/icons/sihterica-192-v2.png'; }} /> : <span style={{ background: edit.primaryColor }}>{detail.displayName.slice(0, 2).toUpperCase()}</span>}
                  <div><h3>{detail.legalName}</h3><p>{detail.oib} · {detail.hostname}</p><span className={`status status--${detail.status.toLowerCase()}`}><i /> {statusLabel(detail.status)}</span></div>
                </div>

                <form onSubmit={saveDetails}>
                  <DrawerSection title="Logo i vizualni identitet">
                    <div className="brand-editor">
                      <label className="logo-picker logo-picker--small"><ImagePlus /><span><strong>{edit.logoName || 'Promijeni logo'}</strong><small>PNG, JPG ili WebP</small></span><input type="file" accept="image/png,image/jpeg,image/webp" onChange={(event) => void imageFromInput(event).then((image) => setEdit((current) => current && ({ ...current, logoDataUrl: image.dataUrl, logoName: image.name, removeLogo: false }))).catch((caught) => setDetailError(caught.message))} /></label>
                      {(detail.logoUrl || edit.logoDataUrl) && <button type="button" className="text-button danger-text" onClick={() => setEdit({ ...edit, logoDataUrl: '', logoName: '', removeLogo: true })}><Trash2 size={15} /> Makni logo</button>}
                    </div>
                    <div className="form-grid">
                      <ColorField label="Primarna boja" value={edit.primaryColor} onChange={(value) => setEdit({ ...edit, primaryColor: value })} />
                      <ColorField label="Naglasna boja" value={edit.secondaryColor} onChange={(value) => setEdit({ ...edit, secondaryColor: value })} />
                    </div>
                  </DrawerSection>
                  <DrawerSection title="Podaci i kontakt">
                    <div className="form-grid">
                      <Field wide label="Pravni naziv"><input value={edit.legalName} onChange={(e) => setEdit({ ...edit, legalName: e.target.value })} required /></Field>
                      <Field wide label="Naziv u aplikaciji"><input value={edit.displayName} onChange={(e) => setEdit({ ...edit, displayName: e.target.value })} required /></Field>
                      <Field wide label="Adresa"><input value={edit.address} onChange={(e) => setEdit({ ...edit, address: e.target.value })} required /></Field>
                      <Field label="Poštanski broj"><input value={edit.postalCode} onChange={(e) => setEdit({ ...edit, postalCode: e.target.value })} required /></Field>
                      <Field label="Mjesto"><input value={edit.city} onChange={(e) => setEdit({ ...edit, city: e.target.value })} required /></Field>
                      <Field wide label="Kontakt osoba"><input value={edit.contactName} onChange={(e) => setEdit({ ...edit, contactName: e.target.value })} /></Field>
                      <Field label="E-mail"><input type="email" value={edit.contactEmail} onChange={(e) => setEdit({ ...edit, contactEmail: e.target.value })} /></Field>
                      <Field label="Telefon"><input value={edit.contactPhone} onChange={(e) => setEdit({ ...edit, contactPhone: e.target.value })} /></Field>
                    </div>
                  </DrawerSection>
                  <DrawerSection title="Paket i licenca">
                    <div className="form-grid">
                      <Field label="Paket"><input value={edit.planName} onChange={(e) => setEdit({ ...edit, planName: e.target.value })} required /></Field>
                      <Field label="Limit korisnika"><input type="number" min={1} max={500} value={edit.employeeLimit} onChange={(e) => setEdit({ ...edit, employeeLimit: Number(e.target.value) })} required /></Field>
                      <Field label="Proba do"><CroatianDateInput value={edit.trialEndsAt} onValueChange={(value) => setEdit({ ...edit, trialEndsAt: value })} ariaLabel="Proba do" /></Field>
                      <Field label="Licenca do"><CroatianDateInput value={edit.licenseValidUntil} onValueChange={(value) => setEdit({ ...edit, licenseValidUntil: value })} ariaLabel="Licenca do" /></Field>
                    </div>
                  </DrawerSection>
                  <div className="drawer-actions"><button className="button button--primary" disabled={detailBusy}><Save size={17} /> Spremi promjene</button></div>
                </form>

                <DrawerSection title="Aktivni moduli">
                  <ModuleSelector compact modules={modules} selected={detailModules} onToggle={toggleDetailModule} />
                  <button className="button button--secondary button--full" onClick={() => void saveModules()} disabled={detailBusy}><Save size={17} /> Primijeni paket modula</button>
                </DrawerSection>

                <DrawerSection title="Pristup aplikaciji">
                  <div className="status-actions">
                    <button className="button button--secondary" onClick={() => void changeStatus('ACTIVE')} disabled={detail.status === 'ACTIVE' || detailBusy}><Check /> Aktiviraj</button>
                    <button className="button button--secondary" onClick={() => void changeStatus('SUSPENDED')} disabled={detail.status === 'SUSPENDED' || detailBusy}><PauseCircle /> Obustavi</button>
                    <button className="button button--danger" onClick={() => void changeStatus('CLOSED')} disabled={detail.status === 'CLOSED' || detailBusy}><X /> Zatvori</button>
                  </div>
                </DrawerSection>
                {detailError && <div className="form-error">{detailError}</div>}
              </div>
            ) : (
              <div className="table-empty">Učitavam firmu…</div>
            )}
          </aside>
        </div>
      )}
    </>
  );
}

function SectionTitle({ number, title, text }: { number: string; title: string; text: string }) {
  return <div className="form-section__title"><span>{number}</span><div><h3>{title}</h3><p>{text}</p></div></div>;
}

function DrawerSection({ title, children }: { title: string; children: React.ReactNode }) {
  return <section className="drawer-section"><h3>{title}</h3>{children}</section>;
}

function Field({ label, wide, children }: { label: string; wide?: boolean; children: React.ReactNode }) {
  return <label className={`field ${wide ? 'field--wide' : ''}`}><span>{label}</span><span className="field__control">{children}</span></label>;
}

function ColorField({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return <label className="field"><span>{label}</span><span className="field__control color-field"><input type="color" value={value} onChange={(event) => onChange(event.target.value)} /><code>{value}</code></span></label>;
}

function ModuleSelector({ modules, selected, onToggle, compact = false }: { modules: Module[]; selected: string[]; onToggle: (module: Module) => void; compact?: boolean }) {
  return (
    <div className={`module-selector ${compact ? 'module-selector--compact' : ''}`}>
      {modules.map((module) => {
        const active = selected.includes(module.key) || module.isCore;
        return (
          <button
            type="button"
            key={module.key}
            className={`module-card ${active ? 'is-selected' : ''} ${module.status !== 'ACTIVE' ? 'is-disabled' : ''}`}
            onClick={() => onToggle(module)}
          >
            <span className="module-card__check">{active && <Check size={15} />}</span>
            <strong>{module.name}</strong>
            {!compact && <small>{module.description}</small>}
            <em>{module.isCore ? 'Obavezno' : module.status === 'COMING_SOON' ? 'U pripremi' : `Faza ${module.phase}`}</em>
          </button>
        );
      })}
    </div>
  );
}
